import 'maplibre-gl/dist/maplibre-gl.css';

import './global.scss';
import './tailwind.css';

import {enUS, frFR, itIT, jaJP} from '@clerk/localizations';
import {ClerkProvider} from '@clerk/react';
import IntercomIdentity from 'components/IntercomIdentity';
import Loader from 'components/Loader';
import {PUBLISHABLE_KEY} from 'constants/index';
import {lazy, memo, Suspense} from 'react';
import {useTranslation} from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

const EmbeddedAnalytics = lazy(() => import('subApps/EmbeddedAnalytics'));
const AppRoot = lazy(() => import('subApps/AppRoot'));
const GoRouter = lazy(() => import('subApps/Go'));
const EmbeddedClerkSignIn = lazy(
  () => import('components/Auth/EmbeddedClerkSignIn'),
);
const EmbeddedClerkSignUp = lazy(
  () => import('components/Auth/EmbeddedClerkSignUp'),
);

function BrandwatchRedirect() {
  const location = useLocation();
  const redirectedPath = location.pathname.replace(
    /^\/brandwatch(?=\/|$)/,
    '/cision-sales',
  );
  return (
    <Navigate
      replace
      to={`${redirectedPath}${location.search}${location.hash}`}
    />
  );
}

function AppProviders() {
  const locales = {
    en: enUS,
    fr: frFR,
    it: itIT,
    ja: jaJP,
  };

  const {i18n} = useTranslation();
  const navigate = useNavigate();

  return (
    <Suspense fallback={<Loader />}>
      <ClerkProvider
        // @ts-ignore
        publishableKey={PUBLISHABLE_KEY}
        localization={locales[i18n.language as keyof typeof locales]}
        routerPush={(to: string) => navigate(to)}
        routerReplace={(to: string) => navigate(to, {replace: true})}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
      >
        <IntercomIdentity />
        <Routes>
          {/* Legacy Brandwatch URLs now live under /cision-sales. */}
          <Route element={<BrandwatchRedirect />} path="/brandwatch/*" />
          <Route element={<GoRouter />} path="/go/*" />
          <Route element={<EmbeddedClerkSignIn />} path="/sign-in/*" />
          <Route element={<EmbeddedClerkSignUp />} path="/sign-up/*" />
          <Route element={<EmbeddedAnalytics />} path="/embedded-analytics" />
          <Route element={<AppRoot />} path="*" />
        </Routes>
      </ClerkProvider>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppProviders />
    </BrowserRouter>
  );
}

export default memo(App);
